import { useLocation } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Carousel } from '@factorialco/f0-react/dist/experimental'
import type { AllProductUpdatesQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Buttons from 'modules/product_updates/components/Buttons'
import Media from 'modules/product_updates/components/ListSection/_Item/_Media'
import { useGetProductUpdates } from 'modules/product_updates/hooks/useGetProductUpdates'
import { getColorProductUpdates } from 'modules/product_updates/utils/getColorProductUpdates'

type ProductUpdatesUpdate =
  AllProductUpdatesQuery['productUpdates']['updatesConnection']['edges'][number]['node']

const HeroSection = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const rawUpsellOrigin = queryParams.get('upsell_origin')
  // Remove leading underscore if present
  const upsellOrigin = rawUpsellOrigin?.startsWith('_') ? rawUpsellOrigin.slice(1) : rawUpsellOrigin

  const { productUpdates } = useGetProductUpdates({
    featuredIn: 'hero_home',
  })

  const productUpdateMedia = (
    update: ProductUpdatesUpdate
  ): { alt: string; url: string; video?: boolean }[] => {
    return (
      update.media?.map((item) => {
        const imgObj = {
          url: item.video?.mp4Url || item.url || '',
          alt: '',
          video: !!item.video?.mp4Url || item.url?.includes('.mp4'),
        }
        return imgObj
      }) || []
    )
  }

  const handleMedia = (update: ProductUpdatesUpdate) => {
    const [video] = productUpdateMedia(update)
    return video
  }

  return (
    <Box
      style={{
        marginTop: '-20px',
        position: 'relative',
        background: `radial-gradient(50% 30% at 50% 0%, ${
          getColorProductUpdates(productUpdates[0]?.productModule?.urlIdentifier).gradientStart
        } 0%, rgb(255, 255, 255) 80%)`,
      }}
    >
      <Box paddingTop="s48" paddingBottom="s24">
        <Box gap="s8" width="s400">
          <span className="text-xl font-semibold">{i18n.t('product_updates.hero_home.title')}</span>
          <span className="text-lg font-normal text-f1-foreground-secondary">
            {i18n.t('product_updates.hero_home.subtitle')}
          </span>
        </Box>
      </Box>
      <Carousel
        columns={{
          default: 1,
        }}
        showArrows
        showDots
        autoplay
        delay={5000}
      >
        {productUpdates.map((update) => (
          <div key={update?.id} className="hero-carousel-slide">
            <div className="hero-carousel-slide__media">
              <Media
                video={handleMedia(update) || undefined}
                thumbnail={update?.thumbnail}
                title={update?.title}
                autoPlay
              />
            </div>

            <div className="hero-carousel-slide__content">
              <div style={{ width: 'fit-content' }}>
                <F0TagRaw text={update?.productModule?.name || '-'} />
              </div>
              <div className="hero-carousel-slide__text">
                <span className="text-2xl font-semibold">{update?.title}</span>
                <span className="text-lg font-normal text-f1-foreground-secondary">
                  {update?.shortDescription}
                </span>
              </div>
              <div className="hero-carousel-slide__buttons">
                <Buttons
                  chargebeeModuleName={update?.chargebeeModuleName}
                  productUpdateSlug={update?.slug}
                  isAlreadyReleased
                  productUpdateId={update?.id}
                  featureIdentifier={update?.featureRelated?.featureIdentifier || ''}
                  version="factorial-one"
                  navigationSlug={update?.navigationSlug || undefined}
                  source={`From product news hero ${update?.slug} ${
                    upsellOrigin ? ` - ${upsellOrigin}` : ''
                  }`}
                />
              </div>
            </div>
          </div>
        ))}
      </Carousel>
      <style>{`
        .hero-carousel-slide {
          display: flex;
          flex-direction: row;
          justify-content: center;
          gap: 40px;
          padding: 32px 0;
          align-items: center;
        }
        .hero-carousel-slide__media {
          flex: 1 1 0;
          min-width: 0;
          max-width: 756px;
          aspect-ratio: 16 / 9;
          border-radius: 12px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        .hero-carousel-slide__content {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 24px;
          width: 400px;
          flex-shrink: 0;
        }
        .hero-carousel-slide__text {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .hero-carousel-slide__buttons {
          display: flex;
          flex-direction: row;
          gap: 8px;
        }
        @container (max-width: 720px) {
          .hero-carousel-slide {
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .hero-carousel-slide__media {
            width: 100%;
            flex: 0 0 auto;
            box-shadow: none;
            border-radius: 0;
          }
          .hero-carousel-slide__content {
            width: 100%;
            max-width: 600px;
          }
        }
      `}</style>
    </Box>
  )
}

export default HeroSection
